import * as React from "react";
import {JSONCensus} from "../../processes/massive_actions/CensusCooler/data/models/JSONCensus";
import {useParams} from "react-router-dom";
import {useCensusDetail, useCensusFinalSummary} from "../data/hooks/CensusCoolerHooks";
import {SWRResponse} from "swr";
import {MappedSummaryWithFinalAction} from "../data/types/MappedSummaryWithFinalAction";
import {mapFinalSummaryGroupedByStatusAndStatusDetail} from "../widgets/CensusFinalEquipmentList/utils";
import {
    JSONCensusSuggestedActionsAnalysisSummary
} from "../../processes/massive_actions/CensusCooler/data/models/JSONCensusSuggestedActionsAnalysisSummary";
import {
    useCensusEnums,
    useCensusSuggestedActionsAnalysisSummary
} from "../../processes/massive_actions/CensusCooler/data/hooks/CecnsusCoolerControllerHooks";
import {CensusEnums} from "../../processes/massive_actions/CensusCooler/data/models/CensusEnums";
import {sortSummaryWithFinalAction} from "../utils";
import {LastUserPosition} from "../index";

interface CensusDetailsContextType {
    censusDetail: SWRResponse<JSONCensus, Error, any>
    isLoadingActions: boolean,
    censusGuid: string | undefined;
    mappedSummary: MappedSummaryWithFinalAction[];
    stackManager: any
    suggestedActions: SWRResponse<JSONCensusSuggestedActionsAnalysisSummary, Error, any>,
    censusEnums: SWRResponse<CensusEnums, Error, any>
    updateOpenLevel: (open: boolean) => void;
    updateNavigateToCensusDetail: (value: boolean) => void;
    updateLastUserPosition: (userPosition: LastUserPosition) => void;
    lastUIUserPosition: LastUserPosition
}

export const CensusDetailsContext = React.createContext<CensusDetailsContextType>(
    {} as any,
);

type CensusDetailsProps = {
    stackManager: any;
    updateLastUserPosition: (userPosition: LastUserPosition, replace?: boolean) => void;
    updateOpenLevel: (open: boolean) => void;
    updateNavigateToCensusDetail: (value: boolean) => void;
    lastUIUserPosition: LastUserPosition
};


export const CensusDetailsProvider: React.FC<CensusDetailsProps> = ({
                                                                        stackManager,
                                                                        updateLastUserPosition,
                                                                        updateOpenLevel,
                                                                        lastUIUserPosition,
                                                                        updateNavigateToCensusDetail,
                                                                        children,
                                                                    }) => {
    const {censusGuid} = useParams();
    const suggestedActions = useCensusSuggestedActionsAnalysisSummary(censusGuid, true);
    const censusEnums = useCensusEnums(true);
    const params = React.useMemo(() => {
        if (censusGuid) {
            return {guids: censusGuid, shouldFetch: true}
        }
        return {guids: undefined, shouldFetch: false}
    }, [censusGuid]);
    const censusDetail = useCensusDetail(params.guids, params.shouldFetch);
    const summary = useCensusFinalSummary(params.guids, params.shouldFetch);

    const [mappedSummary, setMappedSummary] = React.useState<MappedSummaryWithFinalAction[]>([]);

    React.useEffect(() => {
        const mappedSummaries = mapFinalSummaryGroupedByStatusAndStatusDetail(summary.data)
            .sort(sortSummaryWithFinalAction);
        setMappedSummary(mappedSummaries);
    }, [summary.data, summary.isLoading])

    React.useEffect(() => {
        if (censusGuid && censusDetail.data && !censusDetail.isLoading) {
            updateLastUserPosition({census: censusDetail.data});
        }
    }, [censusGuid, censusDetail.data, censusDetail.isLoading])

    return (
        <CensusDetailsContext.Provider
            value={{
                censusDetail,
                isLoadingActions: summary.isLoading,
                censusGuid,
                mappedSummary,
                stackManager,
                suggestedActions,
                censusEnums,
                updateOpenLevel,
                updateLastUserPosition,
                lastUIUserPosition,
                updateNavigateToCensusDetail
            }}
        >
            {children}
        </CensusDetailsContext.Provider>
    );
};
